import React, { Suspense } from "react";
import CanPerform from "../../roles/CanPerform";
import Sidebar from "../sidebar/Sidebar";
import { isLogin } from "../../utils/local-storage.util";
import Page404 from "../../shared/components/default/Page404";
import Topbar from "../topbar/Topbar";
import SuspenseLoader from "../loader/SuspenseLoader";
import { CompatRoute, Navigate } from "react-router-dom-v5-compat";
import { Route } from "react-router-dom";
import useFeaturebase from "../../hooks/useFeaturebase";
import useFeaturebaseIdentity from "../../hooks/useFeaturebaseIdentity";
import config from "../../config";

/**
 * Private route component - only for logged user
 *
 * @param {string} title - Route title
 * @param {Array} requiredPermissions - required permissions array
 * @param {Array} permission - permissions array
 * @param {React.ReactNode} component - Component
 * @param {boolean} hideOutletPadding - Used to trigger hide/show outlet, Default: false
 * @param {React.ReactNode} rest - Route Details
 *
 * @return {React.FC} Private Route component
 *
 */
const PrivateRoute = ({
	hideOutletPadding = false,
	requiredPermissions,
	component: Component,
	title,
	permission,
	priorityAccess = false,
	catchAllRoutes = false,
	...rest
}) => {
	document.title = title ? title + " - Bimify" : "Bimify";

	const isAuthenticated = isLogin();

	const featurebaseEnabled =
		isAuthenticated && !!config.features.featurebase.enabled;
	const featurebaseIdentity = useFeaturebaseIdentity(featurebaseEnabled);

	useFeaturebase({
		// Boot only once the identity request has resolved: with jwtToken when
		// available (identified user), or without it as an anonymous fallback.
		bootOptions: featurebaseIdentity.resolved
			? {
					appId: config.features.featurebase.appId || "",
					...(featurebaseIdentity.token
						? { featurebaseJwt: featurebaseIdentity.token }
						: {}),
			  }
			: undefined,
		enabled: featurebaseEnabled,
		removeOnDisable: true,
	});

	const renderComponent = (props) => {
		if (isAuthenticated) {
			return (
				<>
					<Sidebar />
					<CanPerform
						permission={permission}
						requiredPermissions={requiredPermissions}
						priorityAccess={priorityAccess}
						yes={() => {
							return (
								<>
									<main className="main main--has-sidebar">
										<Topbar />
										<Suspense fallback={<SuspenseLoader />}>
											<div className="outlet">
												{!hideOutletPadding ? (
													<Component {...props} />
												) : (
													<Component {...props} />
												)}
											</div>
										</Suspense>
									</main>
								</>
							);
						}}
						no={() => {
							return (
								<>
									<main className="main main--has-sidebar">
										<Topbar />
										<Page404 />
									</main>
								</>
							);
						}}
					/>
				</>
			);
		} else {
			return <Navigate to="/login" />;
		}
	};

	if (catchAllRoutes) {
		return <Route {...rest} component={renderComponent} />;
	}
	return <CompatRoute {...rest} render={renderComponent} />;
};

export default PrivateRoute;
